import React, { useState, useCallback } from "react";
import { Box, Button, CircularProgress, IconButton, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useDispatch, useSelector } from "react-redux";
import Pending from "./Pending";
import Completed from "./Completed";
import Trades from "./Trades";
import GTT from "./GTT";
import PageTabs from "../Common/PageTabs";
import {
  fetchPendingOrdersAction,
  fetchCompletedOrdersAction,
  fetchTradesAction,
  fetchGTTOrdersAction,
} from "../../store/actions/orders";
const CACHE_KEY = "pending_orders_cache";

const Orders = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const handleChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const brokerSettings = useSelector((state) => state.profile.brokerSettings || {});
  const isGTTEnabled = String(brokerSettings.gttenabled).toLowerCase() === "true";

  const orderTabs = [
    { label: "Pending", value: 0 },
    { label: "Completed", value: 1 },
    { label: "Trades", value: 2 },
    ...(isGTTEnabled ? [{ label: "GTT", value: 3 }] : []),
  ];

  // Refresh the data for whichever tab is currently active. Tracks a spinning
  // state so the button shows activity instead of looking inert/disabled.
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    // Keep the loader visible for at least a beat so fast responses still
    // give clear "it was triggered" feedback instead of flashing.
    const minVisible = new Promise((resolve) => setTimeout(resolve, 600));
    try {
      let fetchPromise;
      if (activeTab === 0) fetchPromise = dispatch(fetchPendingOrdersAction());
      else if (activeTab === 1) fetchPromise = dispatch(fetchCompletedOrdersAction());
      else if (activeTab === 2) fetchPromise = dispatch(fetchTradesAction());
      else if (activeTab === 3 && isGTTEnabled) fetchPromise = dispatch(fetchGTTOrdersAction());
      await Promise.all([fetchPromise, minVisible]);
    } finally {
      setRefreshing(false);
    }
  }, [activeTab, dispatch]);

  React.useEffect(() => {
    return () => {
      localStorage.removeItem(CACHE_KEY);
    };
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: "background.default",
        [theme.breakpoints.down('sm')]: {
          // 56 AppBar + 30 Top Ticker = 86 top; + 56 BottomNav = 142
          height: 'calc(100vh - 142px)',
          position: 'fixed',
          top: 86,
          left: 0,
          right: 0,
          bottom: 56
        }
      }}
    >
      <PageTabs
        value={activeTab}
        onChange={handleChange}
        tabs={orderTabs}
        sticky
        action={
          isMobile ? (
            <Tooltip title={refreshing ? "Refreshing…" : "Refresh"}>
              <IconButton onClick={handleRefresh} size="small" color="primary">
                {refreshing ? (
                  <CircularProgress size={18} thickness={5} color="primary" />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              startIcon={
                refreshing ? (
                  <CircularProgress size={16} thickness={5} color="inherit" />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={handleRefresh}
              size="small"
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
          )
        }
      />
      <Box
        sx={{
          flex: 1,
          height: isMobile ? 'calc(100% - 44px)' : 'calc(100vh - 44px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'none',
            p: activeTab === 3 ? 0 : { xs: 0.5, sm: 1 },
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            pb: isMobile ? '56px' : 0
          }}
        >
          {activeTab === 0 && <Pending />}
          {activeTab === 1 && <Completed />}
          {activeTab === 2 && <Trades />}
          {activeTab === 3 && isGTTEnabled && <GTT />}
        </Box>
      </Box>
    </Box>
  );
};

export default Orders;
